import { Input, Popover } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import { useChatbotAttachments } from 'features/chatbot/contexts/ChatbotAttachmentContext'
import {
  EmbeddedDocument,
  useEmbeddedDocuments,
} from 'features/chatbot/hooks/useEmbeddedDocuments'
import { Paperclip } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'
import styled from 'styled-components'

const AttachButtonWrapper = styled.div`
  position: absolute;
  top: 0.75rem;
  left: 0.5rem;
  z-index: 1;
`

const AttachButton = styled(CantileverButton)`
  min-height: 1.5rem;
  width: 1.5rem;
`

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 18rem;
  max-height: 20rem;
`

const SearchInput = styled(Input)`
  margin-bottom: 0.5rem;
`

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.hoverGhostButton};
  }
`

const ITEM_HEIGHT = 40

const EmptyState = styled.div`
  padding: 0.5rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-size: 0.875rem;
  text-align: center;
`

interface DocumentAttachmentPopoverProps {
  onDocumentSelected?: () => void
}

interface RowData {
  documents: EmbeddedDocument[]
  onSelect: (id: string) => void
}

const Row: React.FC<ListChildComponentProps<RowData>> = ({
  index,
  style,
  data,
}) => {
  const doc = data.documents[index]
  return (
    <DocumentItem
      style={style}
      onClick={() => data.onSelect(doc.id)}
      title={doc.name}>
      {doc.name}
    </DocumentItem>
  )
}

export const DocumentAttachmentPopover: React.FC<
  DocumentAttachmentPopoverProps
> = ({ onDocumentSelected }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { documents, isLoading } = useEmbeddedDocuments()
  const attachmentContext = useChatbotAttachments()

  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents
    const query = searchQuery.toLowerCase()
    return documents.filter((doc) => doc.name.toLowerCase().includes(query))
  }, [documents, searchQuery])

  const handleDocumentSelect = (documentId: string) => {
    if (!documentId || !attachmentContext) return
    const doc = documents.find((d) => d.id === documentId)
    if (doc) {
      attachmentContext.addAttachment({
        id: doc.id,
        type: doc.type,
        name: doc.name,
        displayTitle: doc.displayTitle,
      })
    }
    setIsPopoverOpen(false)
    setSearchQuery('')
    onDocumentSelected?.()
  }

  const listHeight = Math.min(filteredDocuments.length * ITEM_HEIGHT, 280)

  return (
    <AttachButtonWrapper>
      <Popover
        content={
          <PopoverContent>
            <SearchInput
              placeholder='Search documents...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
            />
            {isLoading ? (
              <EmptyState>Loading documents...</EmptyState>
            ) : filteredDocuments.length === 0 ? (
              <EmptyState>No documents found</EmptyState>
            ) : (
              <FixedSizeList
                height={listHeight}
                width='100%'
                itemCount={filteredDocuments.length}
                itemSize={ITEM_HEIGHT}
                itemKey={(index, data) => data.documents[index].id}
                itemData={{
                  documents: filteredDocuments,
                  onSelect: handleDocumentSelect,
                }}>
                {Row}
              </FixedSizeList>
            )}
          </PopoverContent>
        }
        trigger='click'
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        placement='top'>
        <span>
          <AttachButton ghost disabled={isLoading} iconOnly>
            <Paperclip size={14} />
          </AttachButton>
        </span>
      </Popover>
    </AttachButtonWrapper>
  )
}
